import { College, CollegeBranding } from '../types';
import { apiClient, handleApiSuccess } from './api';

/**
 * College Service
 * Handles college data storage and retrieval from localStorage
 */
export class CollegeService {
  private static readonly COLLEGE_STORAGE_KEY = 'unii_college_data';

  /**
   * Store college data in localStorage
   */
  static setCollegeData(college: College): void {
    try {
      const collegeData = {
        id: college.id,
        name: college.name,
        logo: college.logo,
        storedAt: new Date().toISOString()
      };
      localStorage.setItem(this.COLLEGE_STORAGE_KEY, JSON.stringify(collegeData));
    } catch (error) {
      console.error('Failed to store college data:', error);
    }
  }

  /**
   * Get college data from localStorage
   */
  static getCollegeData(): { id: string; name: string; logo?: string; storedAt: string } | null {
    try {
      const stored = localStorage.getItem(this.COLLEGE_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
      return null;
    } catch (error) {
      console.error('Failed to retrieve college data:', error);
      return null;
    }
  }

  /**
   * Clear college data from localStorage
   */
  static clearCollegeData(): void {
    try {
      localStorage.removeItem(this.COLLEGE_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear college data:', error);
    }
  }

  /**
   * Get college name for branding
   */
  static getCollegeBrandName(): string {
    const collegeData = this.getCollegeData();
    if (collegeData) {
      return `${collegeData.name} × Unii Club`;
    }
    return 'Unii Club';
  }

  /**
   * Get just college name
   */
  static getCollegeName(): string {
    const collegeData = this.getCollegeData();
    return collegeData?.name || 'College';
  }

  /**
   * Check if college data exists and is valid (not older than 24 hours)
   */
  static isCollegeDataValid(): boolean {
    const collegeData = this.getCollegeData();
    if (!collegeData) return false;

    const storedTime = new Date(collegeData.storedAt).getTime();
    const currentTime = new Date().getTime();
    const hoursDiff = (currentTime - storedTime) / (1000 * 60 * 60);

    // Data is valid for 24 hours
    return hoursDiff < 24;
  }

  /**
   * Fetch college data from API and store it
   */
  static async fetchAndStoreCollegeData(collegeId: string): Promise<College> {
    try {
      const response = await apiClient.get(`/colleges/${collegeId}`);
      const college = handleApiSuccess(response);
      this.setCollegeData(college);
      return college;
    } catch (error) {
      console.error('Failed to fetch college data:', error);
      throw error;
    }
  }

  /**
   * Get college data, fetch from API if not cached or expired
   */
  static async getCollegeInfo(collegeId?: string): Promise<College | null> {
    try {
      // Check if we have valid cached data
      if (this.isCollegeDataValid()) {
        const cachedData = this.getCollegeData();
        if (cachedData) {
          // Return cached data as College object (we'd need to expand this based on actual needs)
          return {
            id: cachedData.id,
            name: cachedData.name,
            logo: cachedData.logo,
            // Add other required fields with defaults
            email: '',
            address: '',
            phone: '',
            branches: [],
            statistics: { totalStudents: 0, totalFaculty: 0, totalClubs: 0, activeApplications: 0 },
            settings: { requireStudentApproval: true, requireFacultyApproval: true, allowPublicClubs: false, maxClubsPerStudent: 5 }
          };
        }
      }

      // Fetch fresh data if collegeId is provided
      if (collegeId) {
        return await this.fetchAndStoreCollegeData(collegeId);
      }

      return null;
    } catch (error) {
      console.error('Failed to get college info:', error);
      return null;
    }
  }

  /**
   * Get all colleges for registration dropdown (public endpoint)
   */
  static async getAllColleges(): Promise<College[]> {
    try {
      const response = await apiClient.get('/colleges');
      const colleges = handleApiSuccess(response);

      // Transform _id to id for frontend compatibility
      return (colleges || []).map((college: any) => ({
        ...college,
        id: college._id || college.id,
      }));
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
      return [];
    }
  }

  /**
   * Get college branding information for UI
   */
  static async getCollegeBranding(): Promise<CollegeBranding> {
    try {
      const response = await apiClient.get('/college/branding');
      return handleApiSuccess(response);
    } catch (error) {
      console.error('Failed to fetch college branding:', error);
      // Return default branding
      return {
        collegeName: 'College',
        brandingText: 'Unii Club',
        primaryColor: '#667eea',
        secondaryColor: '#764ba2'
      };
    }
  }

  /**
   * Get college info from new API endpoint
   */
  static async getFullCollegeInfo(): Promise<any> {
    try {
      const response = await apiClient.get('/college/info');
      return handleApiSuccess(response);
    } catch (error) {
      console.error('Failed to fetch college info:', error);
      throw error;
    }
  }

  /**
   * Get college statistics
   */
  static async getCollegeStats(): Promise<any> {
    try {
      const response = await apiClient.get('/college/stats');
      return handleApiSuccess(response);
    } catch (error) {
      console.error('Failed to fetch college stats:', error);
      throw error;
    }
  }

  /**
   * Get available branches
   */
  static async getCollegeBranches(): Promise<string[]> {
    try {
      const response = await apiClient.get('/college/branches');
      const data = handleApiSuccess(response);
      return data.branches || [];
    } catch (error) {
      console.error('Failed to fetch college branches:', error);
      return [];
    }
  }
}

export default CollegeService;